import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import SweetAlert2 from "react-sweetalert2";
import { useAuth } from "../../auth/auth_provider.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

const defaultTheme = createTheme({
  palette: {
    primary: { main: '#1dc39f' },
    secondary: { main: '#8ff9ab' },
  },
  typography: { h5: { fontWeight: 600 } },
});

const KeluhanForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [swalProps, setSwalProps] = useState({});
  const [successSend, setSuccess] = useState(null);
  const data_longwis = location.state?.data_longwis || null;
  const { pic } = useAuth();

  useEffect(() => {
    if (!data_longwis) {
      setSwalProps({
        show: true,
        title: "Error",
        icon: "error",
        text: "Data Longwis tidak ditemukan. Silakan pilih lokasi terlebih dahulu.",
        didClose: () => { navigate('/pic/daftar-longwis-keluhan'); }
      });
    }
  }, [data_longwis, navigate]);

  const [data, setData] = useState({
    keluhan: "",
    provider: "Telkom"
  });

  const sendData = async (event) => {
    event.preventDefault();
    if (!data_longwis) return;

    if (!data.keluhan.trim()) {
      setSwalProps({ show: true, title: "Error", icon: "error", text: "Keluhan wajib diisi." });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}api/keluhan/${data_longwis.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_AUTH
        },
        body: JSON.stringify({
          keluhan: data.keluhan,
          provider: data.provider,
          id_user: pic?.user?.id || undefined
        })
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Gagal menyimpan keluhan');

      setSuccess(true);
      setSwalProps({ show: true, title: "Sukses", icon: "success", text: "Keluhan berhasil dikirim." });
    } catch (err) {
      setSwalProps({ show: true, title: "Error", icon: "error", text: err.message || 'Terjadi kesalahan' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1dc39f 0%, #8ff9ab 100%)', py: 3 }}>
      <ThemeProvider theme={defaultTheme}>
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <SweetAlert2 {...swalProps} didClose={() => { setSwalProps({}); if(successSend){ navigate('/pic/daftar-longwis-keluhan'); } }} />
          <Paper elevation={10} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 3, background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)' }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', backgroundColor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, boxShadow: 3 }}>
              <ReportProblemIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography component="h1" variant="h5" sx={{ mb: 1, color: 'primary.main' }}>Form Keluhan Longwis</Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
              {data_longwis ? `Lokasi: ${data_longwis.nama_lokasi || 'Tidak diketahui'}` : 'Memuat data lokasi...'}
            </Typography>

            {!data_longwis ? (
              <Alert severity="warning" sx={{ width: '100%', mb: 3 }}>
                <Typography>Data lokasi Longwis tidak tersedia. Anda akan diarahkan kembali.</Typography>
              </Alert>
            ) : (
              <Box component="form" onSubmit={sendData} sx={{ width: '100%' }}>
                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Keluhan</Typography>
                    <TextField
                      type="text"
                      fullWidth
                      label="Keluhan"
                      placeholder="Tuliskan keluhan (misal: no internet)"
                      value={data.keluhan}
                      required
                      onChange={(e) => setData({ ...data, keluhan: e.target.value })}
                      multiline
                      minRows={3}
                    />
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Provider</Typography>
                    <FormControl fullWidth>
                      <InputLabel size="small" id="select-provider">Pilih Provider</InputLabel>
                      <Select
                        labelId="select-provider"
                        id="provider"
                        value={data.provider}
                        required
                        label="Pilih Provider"
                        size="small"
                        onChange={(e) => setData({ ...data, provider: e.target.value })}
                      >
                        <MenuItem value="Telkom">Telkom</MenuItem>
                        <MenuItem value="Primahome">Primahome</MenuItem>
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>

                <Button type="submit" fullWidth variant="contained" sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600, borderRadius: 2, background: 'linear-gradient(90deg, #1dc39f, #8ff9ab)', textTransform: 'none', '&:hover': { background: 'linear-gradient(90deg, #17a085, #7ce89a)' } }}>
                  Kirim Keluhan
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default KeluhanForm;
